import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { isEmptyWhereClause } from '@modules/common/utils';
import { SearchQuery } from '@modules/feed/types';
import { deserializeBaseCondition } from '@modules/feed/utils/deserializeBaseCondition';
import { deserializeDuration } from '@modules/feed/utils/deserializeDuration';
import { deserializeWhereClause } from '@modules/feed/utils/deserializeWhereClause';
import { getSearchWhereClauseFromBaseCondition } from '@modules/feed/utils/getSearchWhereClauseFromBaseCondition';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import {
  EmptyWhereClause,
  OperationType,
} from 'v2/core/services/queryBuilder/whereClause';

interface GetSearchQueryFromMetadataOptions {
  metadata?: string;
  getDurationField: boolean;
  defaultTimeRange: DateTimeRange;
}

export const getSearchQueryFromMetadata = ({
  metadata,
  getDurationField,
  defaultTimeRange,
}: GetSearchQueryFromMetadataOptions): Partial<SearchQuery> => {
  const dashboardURL = new URL(metadata ?? '');
  const search = new URLSearchParams(dashboardURL.search);

  const searchDuration = decodeURIComponent(search.get('duration') ?? '');
  const searchBaseCondition = decodeURIComponent(search.get('base') ?? '');
  // There can be cases wherein whereClause has a '%' in it. When that happens,
  // decodeURIComponent throws a error since it always decodes '%' that are followed
  // by two hexadecimal digits which represent a encoded character.
  // For fix we are replacing it with '%25' which decodes to just '%'.
  const searchWhereClause = decodeURIComponent(
    search.get('where')?.replace(/%/g, '%25') ?? '',
  );

  const duration = searchDuration
    ? deserializeDuration(searchDuration)
    : defaultTimeRange;
  const baseCondition = searchBaseCondition
    ? deserializeBaseCondition(searchBaseCondition)
    : null;
  const whereClause = searchWhereClause
    ? deserializeWhereClause(searchWhereClause)
    : EmptyWhereClause;

  const getDerivedSearchWhereClause = (
    baseCondition: SearchQuery['baseCondition'],
    whereClause: SearchQuery['whereClause'],
  ) => {
    if (!baseCondition) {
      return whereClause;
    }

    const baseConditionWhereClause =
      getSearchWhereClauseFromBaseCondition(baseCondition);

    if (whereClause && isEmptyWhereClause(baseConditionWhereClause)) {
      return whereClause;
    }

    if (whereClause && !isEmptyWhereClause(whereClause)) {
      return combineWhereClauses(
        baseConditionWhereClause,
        whereClause,
        OperationType.AND,
      );
    }

    return baseConditionWhereClause;
  };

  const derivedSearchWhereClause = getDerivedSearchWhereClause(
    baseCondition,
    whereClause,
  );

  if (getDurationField) {
    return {
      duration,
      whereClause: derivedSearchWhereClause,
    };
  }

  return {
    whereClause: derivedSearchWhereClause,
  };
};
